import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { ClickableProfilePhoto, PhotoLightbox } from './PhotoLightbox';
import { SubscribeButton } from './SubscribeButton';
import { MessageButton } from './MessageButton';
import { BellButton } from './BellButton';
import { RequestButton } from './RequestButton';
import { ListenerBadge } from './ListenerBadge';
import { WeeklyGrowthBadge } from './LiveActivityStrip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Music, Mic2, Radio, Building2, Camera, Headphones, User, Video,
  MapPin, Share2, UserPlus, UserMinus, Verified, Clock,
  MoreHorizontal, Copy, Flag, Ban, Edit, Play, Crown,
  Sparkles, Shield, Star, Zap, TrendingUp, Eye, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const ROLE_ICONS = {
  listener: User, artist: Music, content_creator: Video, podcaster: Mic2,
  streamer: Radio, studio: Building2, videographer: Camera, engineer: Headphones, owner: Music,
};

const TIER_CONFIG = {
  free:       { color: '#888', glow: 'rgba(136,136,136,0.15)', label: null,         gradient: 'linear-gradient(135deg, #333 0%, #555 100%)' },
  starter:    { color: '#3b82f6', glow: 'rgba(59,130,246,0.35)', label: 'Starter',   gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' },
  growth:     { color: '#10b981', glow: 'rgba(16,185,129,0.35)', label: 'Growth',    gradient: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)' },
  pro:        { color: '#8b5cf6', glow: 'rgba(139,92,246,0.4)',  label: 'Pro',       gradient: 'linear-gradient(135deg, #5b21b6 0%, #8b5cf6 100%)' },
  enterprise: { color: '#f59e0b', glow: 'rgba(245,158,11,0.4)',  label: 'Enterprise', gradient: 'linear-gradient(135deg, #92400e 0%, #f59e0b 100%)' },
  label:      { color: '#fbbf24', glow: 'rgba(251,191,36,0.45)', label: 'Label',     gradient: 'linear-gradient(135deg, #b45309 0%, #fbbf24 50%, #f59e0b 100%)' },
};

const ROLE_LABELS = {
  listener: 'Listener', artist: 'Artist', content_creator: 'Creator', podcaster: 'Podcaster',
  streamer: 'Streamer', studio: 'Studio', videographer: 'Videographer', engineer: 'Engineer', owner: 'Artist',
};

export function PremiumProfileHeader({
  profile, roleProfileData, isOwnProfile, isFollowing, followStatus,
  followerCount, followingCount, tracks,
  onFollow, onShare, onShowFollowers, onShowFollowing, listenerData,
}) {
  const navigate = useNavigate();
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [imgError, setImgError] = useState(false);
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);

  const tier = (profile.artist_tier || 'free').toLowerCase();
  const tierCfg = TIER_CONFIG[tier] || TIER_CONFIG.free;
  const RoleIcon = ROLE_ICONS[profile.role] || User;
  const isCreator = ['artist', 'owner', 'content_creator', 'podcaster', 'streamer', 'videographer', 'producer', 'engineer', 'studio'].includes(profile.role);
  const isPremium = ['pro', 'enterprise', 'label'].includes(tier);
  const avatarSrc = profile.avatar_url ? (profile.avatar_url.startsWith('http') ? profile.avatar_url : `${API.replace('/api', '')}${profile.avatar_url}`) : null;
  const bannerSrc = profile.banner_url ? (profile.banner_url.startsWith('http') ? profile.banner_url : `${API.replace('/api', '')}${profile.banner_url}`) : null;

  const accentColor = tierCfg.color;

  return (
    <div data-testid="premium-profile-header" className="relative">
      {/* ── Inline Styles for glow animations ── */}
      <style>{`
        @keyframes avatar-ring-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulse-glow { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        .avatar-ring-outer {
          position: absolute; inset: -4px; border-radius: 20px; padding: 3px;
          background: ${tierCfg.gradient};
          animation: avatar-ring-spin 8s linear infinite;
          z-index: 0;
        }
        .avatar-ring-outer::after {
          content: ''; position: absolute; inset: 3px; border-radius: 17px;
          background: var(--background, #0B0B0E);
        }
        .avatar-glow {
          position: absolute; inset: -12px; border-radius: 24px;
          background: ${tierCfg.glow};
          filter: blur(18px);
          animation: pulse-glow 3s ease-in-out infinite;
          z-index: -1;
        }
        .stat-btn:hover .stat-value { color: ${accentColor}; }
        .premium-follow-btn {
          background: ${tierCfg.gradient};
          transition: all 0.2s ease;
        }
        .premium-follow-btn:hover { filter: brightness(1.15); transform: translateY(-1px); box-shadow: 0 4px 20px ${tierCfg.glow}; }
        .live-pulse { animation: pulse-glow 1.5s ease-in-out infinite; }
      `}</style>

      {/* ── Banner ── */}
      <div className="relative h-48 sm:h-60 md:h-72 overflow-hidden">
        {bannerSrc ? (
          <img src={bannerSrc} alt="" className="absolute inset-0 w-full h-full object-cover scale-105 blur-[2px] opacity-40" />
        ) : (
          <div className="absolute inset-0" style={{
            background: `linear-gradient(135deg, ${accentColor}15 0%, transparent 40%, ${accentColor}08 70%, transparent 100%)`,
          }}>
            {/* Decorative grid pattern */}
            <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `radial-gradient(${accentColor} 1px, transparent 1px)`,
              backgroundSize: '24px 24px',
            }} />
          </div>
        )}
        {/* Bottom vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/60 via-transparent to-background/40" />
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />

        {/* Edit banner */}
        {isOwnProfile && (
          <button
            onClick={() => navigate('/settings')}
            className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium backdrop-blur-xl transition-all hover:scale-105"
            style={{ background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.8)' }}
            data-testid="edit-banner-btn"
          >
            <Edit className="w-3.5 h-3.5" /> Edit
          </button>
        )}
      </div>

      {/* ── Profile Card ── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-24 relative z-10">
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-center sm:items-end">

          {/* Avatar Module */}
          <div className="relative flex-shrink-0 group" data-testid="premium-avatar-module">
            {/* Glow */}
            <div className="avatar-glow" />
            {/* Rotating ring */}
            <div className="avatar-ring-outer" />

            <div className="relative z-10">
              <ClickableProfilePhoto
                src={avatarSrc}
                className="w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 rounded-2xl overflow-hidden bg-card transition-transform duration-300 group-hover:scale-[1.03]"
                onClick={() => avatarSrc && setShowPhotoLightbox(true)}
              >
                {avatarSrc && !imgError ? (
                  <img
                    src={avatarSrc}
                    alt={profile.name}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-card to-muted">
                    <RoleIcon className="w-14 h-14 sm:w-16 sm:h-16 text-muted-foreground/40" />
                  </div>
                )}
              </ClickableProfilePhoto>
            </div>

            {/* Tier badge */}
            {tierCfg.label && (
              <span
                data-testid="tier-badge"
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-20 px-3 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.15em] whitespace-nowrap backdrop-blur-md border"
                style={{
                  background: `${accentColor}25`,
                  borderColor: `${accentColor}40`,
                  color: accentColor,
                  boxShadow: `0 2px 12px ${tierCfg.glow}`,
                }}
              >
                {tierCfg.label}
              </span>
            )}

            {/* LIVE indicator */}
            {profile.is_live && (
              <span className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white live-pulse" style={{ background: '#ef4444' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping absolute" style={{ left: 6, top: 7 }} />
                LIVE
              </span>
            )}

            {/* Edit avatar overlay */}
            {isOwnProfile && (
              <button
                onClick={() => navigate('/settings')}
                className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl transition-all opacity-0 group-hover:opacity-100"
                style={{ background: 'rgba(0,0,0,0.45)' }}
                data-testid="edit-avatar-btn"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            )}
          </div>

          {/* ── Info Column ── */}
          <div className="flex-1 min-w-0 text-center sm:text-left pb-1">
            {/* Name row */}
            <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start mb-1">
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-foreground leading-tight">
                {profile.stage_name || profile.name}
              </h1>
              {profile.is_verified && (
                <Verified className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: '#60a5fa' }} />
              )}
              {isPremium && (
                <Crown className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
              )}
            </div>

            {/* Handle + Role badges */}
            <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start mb-3">
              {profile.handle && (
                <span className="text-sm text-muted-foreground font-medium">{profile.handle ? `@${profile.handle}` : ""}</span>
              )}
              <Badge variant="outline" className="text-[10px] px-2 py-0 h-5 border-border/50 text-muted-foreground bg-transparent">
                <RoleIcon className="w-3 h-3 mr-1" />
                {ROLE_LABELS[profile.role] || 'Member'}
              </Badge>
              {profile.xp_level && (
                <Badge variant="outline" className="text-[10px] px-2 py-0 h-5" style={{
                  background: `${accentColor}12`,
                  borderColor: `${accentColor}30`,
                  color: accentColor,
                }}>
                  Lv.{profile.xp_level}
                </Badge>
              )}
              {listenerData && listenerData.listener_tier !== 'free' && (
                <ListenerBadge tier={listenerData.listener_tier} level={listenerData.listener_level} size="sm" showLabel />
              )}
            </div>

            {/* Genre + Location */}
            {(profile.genre || profile.primary_genre || profile.neighborhood || profile.city || profile.location) && (
              <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start mb-3 text-xs">
                {(profile.genre || profile.primary_genre) && (
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-border/40 text-muted-foreground" style={{
                    background: `${accentColor}08`,
                  }}>
                    <Music className="w-3 h-3" style={{ color: accentColor }} /> {profile.genre || profile.primary_genre}
                  </span>
                )}
                {(profile.neighborhood || profile.city || profile.location) && (
                  <span className="flex items-center gap-1 text-muted-foreground/70">
                    <MapPin className="w-3 h-3" /> {profile.neighborhood || profile.city || profile.location}
                  </span>
                )}
              </div>
            )}

            {/* Bio */}
            {profile.bio && (
              <p className="text-sm text-muted-foreground/80 mb-0 max-w-xl leading-relaxed line-clamp-3">{profile.bio}</p>
            )}
          </div>
        </div>

        {/* ── Stats Bar ── */}
        <div className="mt-6 flex items-center gap-0.5 flex-wrap justify-center sm:justify-start" data-testid="profile-stats-bar">
          <button onClick={onShowFollowers} className="stat-btn flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl transition-colors hover:bg-muted/20" data-testid="followers-count">
            <span className="stat-value font-heading text-lg font-black text-foreground transition-colors">{followerCount.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground/70 uppercase tracking-wide font-medium">Followers</span>
          </button>
          <span className="w-px h-5 bg-border/40 mx-0.5" />
          <button onClick={onShowFollowing} className="stat-btn flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl transition-colors hover:bg-muted/20" data-testid="following-count">
            <span className="stat-value font-heading text-lg font-black text-foreground transition-colors">{followingCount.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground/70 uppercase tracking-wide font-medium">Following</span>
          </button>
          {isCreator && (
            <>
              <span className="w-px h-5 bg-border/40 mx-0.5" />
              <div className="flex items-center gap-1.5 px-3.5 py-2.5" data-testid="tracks-count">
                <span className="font-heading text-lg font-black text-foreground">{tracks?.length || profile.track_count || 0}</span>
                <span className="text-xs text-muted-foreground/70 uppercase tracking-wide font-medium">Tracks</span>
              </div>
              <span className="w-px h-5 bg-border/40 mx-0.5" />
              <div className="flex items-center gap-1.5 px-3.5 py-2.5" data-testid="plays-count">
                <span className="font-heading text-lg font-black text-foreground">
                  {(profile.total_plays || tracks?.reduce((s, t) => s + (t.plays || 0), 0) || 0).toLocaleString()}
                </span>
                <span className="text-xs text-muted-foreground/70 uppercase tracking-wide font-medium">Plays</span>
              </div>
            </>
          )}
        </div>

        {/* ── Weekly Growth ── */}
        <div className="mt-2 flex justify-center sm:justify-start">
          <WeeklyGrowthBadge userId={profile.user_id} />
        </div>

        {/* ── Action Buttons ── */}
        <div className="mt-4 flex items-center gap-2.5 flex-wrap justify-center sm:justify-start" data-testid="profile-action-buttons">
          {!isOwnProfile ? (
            <>
              {/* Follow */}
              {isFollowing || followStatus === 'request_pending' ? (
                <Button
                  onClick={onFollow}
                  variant="outline"
                  data-testid="follow-button"
                  className="border-border/60 text-foreground hover:bg-muted/30 font-heading font-bold text-xs uppercase tracking-wider px-5 h-9 rounded-xl"
                >
                  {isFollowing ? (
                    <><UserMinus className="w-3.5 h-3.5 mr-1.5" /> Following</>
                  ) : (
                    <><Clock className="w-3.5 h-3.5 mr-1.5" /> Requested</>
                  )}
                </Button>
              ) : (
                <button
                  onClick={onFollow}
                  data-testid="follow-button"
                  className="premium-follow-btn flex items-center gap-1.5 px-5 h-9 rounded-xl text-white font-heading font-bold text-xs uppercase tracking-wider"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Follow
                </button>
              )}

              {/* Bell */}
              <BellButton creatorUserId={profile.user_id} isFollowing={isFollowing} />

              {/* Message */}
              <MessageButton
                targetUserId={profile.user_id}
                targetName={profile.name || profile.stage_name || 'this user'}
                variant="outline"
                size="default"
                showText={true}
                className="border-border/60 text-foreground hover:bg-muted/30 font-heading font-bold text-xs uppercase tracking-wider rounded-xl h-9"
              />

              {/* Request */}
              {isCreator && (
                <RequestButton
                  targetType={profile.role === 'artist' || profile.role === 'owner' ? 'artist' : profile.role}
                  targetId={profile.user_id}
                  targetName={profile.name || profile.stage_name}
                  creatorName={profile.name || profile.stage_name}
                  variant="outline"
                  size="default"
                  className="border-border/60 text-foreground hover:bg-muted/30 font-heading font-bold text-xs uppercase tracking-wider rounded-xl h-9"
                />
              )}

              {/* Subscribe */}
              {profile.role && ['artist', 'podcaster', 'streamer', 'producer', 'owner'].includes(profile.role.toLowerCase()) && (
                <SubscribeButton
                  artistId={profile.artist_id || profile.user_id}
                  artistName={profile.name || profile.stage_name || 'this creator'}
                  size="default"
                />
              )}
            </>
          ) : (
            <Button
              variant="outline"
              onClick={() => navigate('/settings')}
              className="border-border/60 text-foreground hover:bg-muted/30 font-heading font-bold text-xs uppercase tracking-wider px-5 h-9 rounded-xl"
              data-testid="edit-profile-btn"
            >
              <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit Profile
            </Button>
          )}

          {/* Share */}
          <Button variant="outline" size="icon" onClick={onShare} className="border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/30 h-9 w-9 rounded-xl" data-testid="share-btn">
            <Share2 className="w-4 h-4" />
          </Button>

          {/* More */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/30 h-9 w-9 rounded-xl" data-testid="profile-more-options">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-card border-border">
              <DropdownMenuItem
                onClick={() => { navigator.clipboard.writeText(window.location.href).catch(() => {}); toast.success('Profile link copied!'); }}
                data-testid="copy-profile-link"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy Profile Link
              </DropdownMenuItem>
              {currentUser && currentUser.user_id !== profile.user_id && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/users/${profile.user_id}/report`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                          body: JSON.stringify({ reason: 'inappropriate_content' }),
                        });
                        if (res.ok) toast.success("Report submitted");
                        else toast.error('Could not submit report');
                      } catch { toast.error('Failed'); }
                    }}
                    data-testid="report-user"
                  >
                    <Flag className="w-4 h-4 mr-2" /> Report User
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/users/${profile.user_id}/block`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                        });
                        if (res.ok) toast.success('User blocked');
                        else toast.error('Could not block user');
                      } catch { toast.error('Failed'); }
                    }}
                    className="text-red-500 focus:text-red-500"
                    data-testid="block-user"
                  >
                    <Ban className="w-4 h-4 mr-2" /> Block User
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Social Links */}
        {profile.socials && Object.keys(profile.socials).length > 0 && (
          <div className="mt-4 flex items-center gap-2 flex-wrap justify-center sm:justify-start" data-testid="social-links">
            {Object.entries(profile.socials).filter(([, url]) => url).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg border border-border/30 text-muted-foreground hover:text-foreground hover:border-border/60 transition-all text-xs font-medium"
                style={{ background: `${accentColor}06` }}
              >
                {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Photo Lightbox */}
      {showPhotoLightbox && avatarSrc && (
        <PhotoLightbox
          userId={profile.user_id}
          userName={profile.name}
          initialPhotoUrl={avatarSrc}
          onClose={() => setShowPhotoLightbox(false)}
        />
      )}
    </div>
  );
}
